// ─────────────────────────────────────────────────────────────────────────
//  ClickViewer · web · 폰이냐 아니냐 — **분기는 여기 한 곳에만 있다**
//
//  ★ 이 파일이 따로 있는 이유가 그 한 줄이다.
//    같은 판단(`화면이 좁은가`)이 여러 곳에 흩어지면 **반드시 값이 갈린다.**
//    실제로 옛 코드에는 분기점이 **넷**이었다:
//
//        576px  Shell.tsx      `visibleFrom="xs"`
//        720px  config.ts:70   `PHONE_MAX_WIDTH_PX` → useIsPhone()
//        768px  theme.ts       `breakpoints.sm` (통일 목표라고 **선언만** 해 둔 값)
//        820px  SCREEN-DESIGN.html:87,138  `.mock-body` 접힘
//
//    구체적 파손: 폭 750px(세로 아이패드·폴드 펼침)에서 목업 grid 는 **한 칸으로 접히는데**
//    `useIsPhone()` 은 720px 기준이라 **false** 다. 그러면 폰용 하단 고정 바가 안 붙어
//    **한 칸으로 길게 늘어난 폼의 맨 아래에 주동작이 파묻힌다.** 감리가 아이패드로 열면
//    「현장 만들기」 버튼을 못 찾는다. 그런 화면은 아무도 버그로 신고하지 않고,
//    그냥 "이 앱은 좀 이상하다" 가 된다.
//
//  ★★ 그래서 규칙이 둘이다 ─────────────────────────────────────────────
//
//    ① **레이아웃이 접히는 것 자체는 JS 로 판정하지 마라.** CSS 로 해라.
//       `.cv-form-grid`(한 칸 접힘)와 `.cv-bottombar`(하단 고정)가 global.css 의
//       **같은 미디어쿼리 안에서** 같이 켜진다. 값이 갈릴 자리가 애초에 없다.
//       → 폼·하단 바를 만들 때 useIsPhone() 을 쓰지 마라. 클래스만 붙여라.
//
//    ② **DOM 구조 자체가 달라질 때만** 이 훅을 써라(표 ↔ 카드처럼).
//       CSS `display:none` 으로 둘 다 그리면 안 보이는 표가 DOM 에 남아
//       스크린리더가 두 번 읽고, 폰에서 쓸데없는 렌더 비용이 든다.
//
//  ⚠️ 새 화면에서 useMediaQuery 를 직접 부르지 마라. 숫자를 새로 쓰는 순간 다섯 번째
//     분기점이 생긴다.
// ─────────────────────────────────────────────────────────────────────────

import { useEffect } from 'react';
import { useMediaQuery } from '@mantine/hooks';

/**
 * 폰으로 볼 최대 폭(px).
 *
 * ★ theme.ts 의 `breakpoints.sm = 48em = 768px` 바로 아래 값이다.
 *   768 로 고른 이유: **세로 아이패드(768)가 데스크톱 쪽에 붙어야** 표가 제 값을 한다.
 *
 * ⚠️ 이 값은 global.css 의 `--cv-phone-max` 와 **같아야 한다.** 아래 개발용 검사가
 *   어긋남을 콘솔에 알린다 — 사람이 기억으로 지키는 규칙은 안 지켜진다.
 *
 * ⚠️ config.ts 의 `PHONE_MAX_WIDTH_PX`(720)는 **더 이상 쓰지 않는다.** 그 상수를
 *   다시 import 하지 마라. 지우는 것은 config.ts 를 소유한 쪽에 맡긴다.
 */
export const PHONE_MAX_PX = 767;

/** useMediaQuery 에 넘길 문자열. 화면마다 다시 조립하지 마라. */
export const PHONE_MEDIA = `(max-width: ${PHONE_MAX_PX}px)`;

/**
 * 지금 폰 폭인가.
 *
 * ⚠️ 「폰인가」가 아니라 **「폭이 좁은가」**다. 데스크톱 브라우저를 확대 200% 하면
 *    1280px 화면도 640 CSS px 이 되어 여기서 true 가 된다. 그게 맞다 —
 *    확대해서 쓰는 사람에게 필요한 배치는 폰과 같다(WCAG 1.4.10).
 */
export function useIsPhone(): boolean {
  useCssBreakpointAgreement();

  return (
    useMediaQuery(PHONE_MEDIA, false, {
      // ★ 첫 렌더에서 곧바로 matchMedia 를 읽는다.
      //   기본값(true)이면 첫 렌더는 무조건 「폰 아님」으로 그렸다가 effect 에서 뒤집힌다.
      //   폰에서 표가 한 번 번쩍이고 카드로 바뀌는데, 그게 느린 기기에서는 눈에 보인다.
      getInitialValueInEffect: false,
    }) ?? false
  );
}

/**
 * 개발 중에만 도는 검사 — CSS 와 JS 의 분기점이 어긋났는지 본다.
 *
 * ★ 이게 있는 이유: 어긋나도 **화면은 대부분 멀쩡해 보인다.** 어긋난 구간(예: 750px)에
 *   들어가야만 「머리글은 표인데 본문은 카드」가 나오고, 그 폭으로 여는 사람은 드물다.
 *   즉 **아무도 안 보는 창에서만 깨지는 버그**라 사람 눈으로는 절대 못 잡는다.
 *   그래서 기계가 잡게 했다.
 *
 * 실서버 빌드에서는 `import.meta.env.DEV` 가 false 라 통째로 사라진다(트리셰이킹).
 */
function useCssBreakpointAgreement(): void {
  // ⚠️ 의존성 배열이 **없다.** 뷰어 CSS 는 뷰어 화면에 들어가야 붙으므로, 첫 마운트에만
  //    보면 정작 검사해야 할 스타일시트를 놓친다. 대신 두 검사 모두 안에서 「이미 봤다」를
  //    확인하고 곧바로 빠져나온다 — 두 번째부터는 숫자 비교 한 번이다.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    checkPhoneMaxVariable();
    scanStylesheetsForStrayBreakpoints();
  });
}

let variableChecked = false;

function checkPhoneMaxVariable(): void {
  if (variableChecked) return;
  // ⚠️ getComputedStyle 은 스타일 재계산을 강제한다. 한 번 성공하면 다시 안 부른다.
  const css = getComputedStyle(document.documentElement).getPropertyValue('--cv-phone-max').trim();
  // 변수가 없으면(=global.css 가 아직 안 붙었으면) 「봤다」고 치지 않는다. 다음 렌더에 다시 본다.
  if (!css) return;
  variableChecked = true;
  if (css !== `${PHONE_MAX_PX}px`) {
    // oxlint-disable-next-line no-console
    console.error(
      `[cv] 분기점이 어긋났다: breakpoint.ts=${PHONE_MAX_PX}px · global.css --cv-phone-max=${css}. ` +
        '이 상태로는 그 사이 폭에서 머리글과 본문이 서로 다른 배치가 된다. 둘 중 하나를 고쳐라.',
    );
  }
}

/**
 * ★★ 위 검사만으로는 **뷰어를 못 본다.** ★★
 *
 * `--cv-phone-max` 는 global.css 가 스스로 내놓은 값이라, 그 변수를 안 쓰는 파일이
 * 자기만의 분기점을 갖고 있어도 검사에 안 걸린다. 실제로 그랬다 [실측 2026-08-07]:
 *
 *     760px  viewer/config.ts:89 · viewer/panels/viewer.module.css:788, 1021
 *     767px  breakpoint.ts · global.css (3곳)
 *     720px  config.ts:70 PHONE_MAX_WIDTH_PX (아무도 안 쓰는 죽은 값)
 *
 * 761~767px 에서 뷰어와 앱의 판정이 갈린다. 앞의 검사는 그 사실을 **한 번도 말하지 않았다.**
 *
 * ⚠️ 그래서 「변수를 하나 더 만들어 대조하자」로 가면 안 된다. 그건 **손으로 베낀 숫자를
 *    하나 더 만드는 것**이고, 지금 고치려는 문제가 정확히 그것이다.
 * → 대신 **문서에 실제로 붙어 있는 CSS 를 통째로 읽는다.** 누가 어느 파일에 몇을 쓰든,
 *   폰/PC 를 가르는 폭이 두 개 이상이면 그 자리에서 걸린다. 베낄 숫자가 없다.
 *
 * 판단 기준:
 *   · `px` 단위 `max-width` 만 본다. `em`(Mantine breakpoints)은 확대 배율에 따라 움직이는
 *     다른 종류의 값이라 여기서 비교하면 거짓 경보만 난다.
 *   · **480~900px 구간**만 본다. 그 밖(예: 1200px 인쇄용, 380px 초소형)은 폰/PC 분기가
 *     아니라 다른 목적의 조정이다.
 *
 * ⚠️ 개발 서버는 CSS 를 같은 출처의 `<style>` 로 꽂으므로 `cssRules` 를 읽을 수 있다.
 *    그래도 try/catch 를 빼지 마라 — 다른 출처 스타일시트 하나가 섞이는 순간
 *    SecurityError 로 **앱이 죽는다.** 검사가 앱을 죽이면 검사가 지워진다.
 */
let scannedSheetCount = -1;
const shoutedWidths = new Set<number>();

function scanStylesheetsForStrayBreakpoints(): void {
  // 스타일시트가 늘어났을 때만 다시 훑는다(뷰어 CSS 는 뷰어에 들어가야 붙는다).
  if (document.styleSheets.length === scannedSheetCount) return;
  scannedSheetCount = document.styleSheets.length;

  const found = new Set<number>();
  for (const sheet of Array.from(document.styleSheets)) {
    let rules: CSSRuleList;
    try {
      rules = sheet.cssRules;
    } catch {
      continue; // 다른 출처 — 읽을 수 없다. 그것뿐이다.
    }
    collectMaxWidths(rules, found);
  }

  for (const px of found) {
    if (px === PHONE_MAX_PX || shoutedWidths.has(px)) continue;
    shoutedWidths.add(px);
    // oxlint-disable-next-line no-console
    console.error(
      `[cv] 폰 분기점이 두 개다: breakpoint.ts=${PHONE_MAX_PX}px 인데 CSS 에 ` +
        `(max-width: ${px}px) 가 있다. ${Math.min(px, PHONE_MAX_PX) + 1}~${Math.max(px, PHONE_MAX_PX)}px ` +
        '에서 한쪽은 폰, 다른 쪽은 PC 로 그려진다. ' +
        '뷰어에서 이게 나면 **패널은 접혔는데 그 패널을 여는 단추가 display:none** 인 화면이 된다 ' +
        '(viewer/config.ts:83-88 의 실측). 그 짝은 viewer/config.ts 의 PHONE_MAX_WIDTH 와 ' +
        'viewer/panels/viewer.module.css 두 곳이므로 **셋을 같이** 옮겨야 한다.',
    );
  }
}

const MAX_WIDTH_PX = /max-width:\s*(\d+(?:\.\d+)?)px/g;

function collectMaxWidths(rules: CSSRuleList, out: Set<number>): void {
  for (const rule of Array.from(rules)) {
    // CSSMediaRule 은 conditionText 를, 중첩 규칙은 자식 rules 를 갖는다.
    const condition = (rule as CSSMediaRule).conditionText;
    if (typeof condition === 'string') {
      MAX_WIDTH_PX.lastIndex = 0;
      let m: RegExpExecArray | null;
      while ((m = MAX_WIDTH_PX.exec(condition)) !== null) {
        const px = Number(m[1]);
        if (px >= 480 && px <= 900) out.add(px);
      }
    }
    const nested = (rule as CSSGroupingRule).cssRules;
    if (nested) collectMaxWidths(nested, out);
  }
}
